import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faClipboardCheck,
  faExclamationTriangle,
  faFilter,
  faHistory,
  faSearch,
  faSpinner,
  faSyncAlt,
  faTimes,
  faWrench,
} from "@fortawesome/free-solid-svg-icons";
import {
  executeDprValidationSupportUpdate,
  getDprValidationSupportAuditLogs,
  getDprValidationSupportRecords,
  previewDprValidationSupportUpdate,
} from "../../services/dprValidationSupportApi";

const defaultFilters = {
  search: "",
  upload_id: "",
  trans_no: "",
  loa_no: "",
  validate_status: "",
  remarks: "",
  physician: "",
  payment_ref_no: "",
  validated_by: "",
  uploaded_by: "",
  validation_link_status: "all",
  date_from: "",
  date_to: "",
};

const defaultSummary = {
  total_rows: 0,
  active_validation_rows: 0,
  inactive_validation_rows: 0,
  missing_validation_rows: 0,
};

const fieldOptions = [
  { field: "remarks", label: "Remarks", nullable: false },
  { field: "physician", label: "Physician", nullable: true },
  { field: "payment_ref_no", label: "Payment Ref. No.", nullable: true },
];

const sortOptions = [
  { value: "processed_at", label: "Processed At" },
  { value: "id", label: "DPR Upload Row ID" },
  { value: "row_number", label: "CSV Row" },
  { value: "upload_id", label: "Upload ID" },
  { value: "trans_no", label: "Trans No." },
  { value: "loa_no", label: "LOA No." },
  { value: "validate_status", label: "Validate Status" },
  { value: "remarks", label: "Remarks" },
  { value: "physician", label: "Physician" },
  { value: "payment_ref_no", label: "Payment Ref. No." },
  { value: "validated_by", label: "Validated By" },
  { value: "uploaded_by", label: "Uploaded By" },
];

const linkStatusOptions = [
  { value: "all", label: "All" },
  { value: "active", label: "Active in DPR" },
  { value: "inactive", label: "Inactive" },
  { value: "missing", label: "Missing link" },
];

const defaultAuditFilters = {
  target_field: "",
  status: "",
  requested_by: "",
  date_from: "",
  date_to: "",
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-PH", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (Number.isNaN(number)) return String(value);
  return number.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.description ||
  error?.message ||
  fallback;

const linkStatusBadge = (status) => {
  const base = "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium";
  if (status === "active") return `${base} bg-emerald-100 text-emerald-700`;
  if (status === "inactive") return `${base} bg-amber-100 text-amber-700`;
  return `${base} bg-red-100 text-red-700`;
};

const DprValidationSupportPage = () => {
  const [activeTab, setActiveTab] = useState("records");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(defaultSummary);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortBy, setSortBy] = useState("processed_at");
  const [sortDir, setSortDir] = useState("desc");

  const [updateMode, setUpdateMode] = useState("selected");
  const [targetField, setTargetField] = useState("remarks");
  const [newValue, setNewValue] = useState("");
  const [shouldSetNull, setShouldSetNull] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const [auditRows, setAuditRows] = useState([]);
  const [auditFilters, setAuditFilters] = useState(defaultAuditFilters);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  const [auditTotalRecords, setAuditTotalRecords] = useState(0);
  const [auditLoading, setAuditLoading] = useState(false);
  const [expandedAuditId, setExpandedAuditId] = useState(null);

  const selectedFieldMeta = useMemo(
    () => fieldOptions.find((item) => item.field === targetField) || fieldOptions[0],
    [targetField]
  );

  const hasAppliedFilters = useMemo(
    () =>
      Object.entries(appliedFilters).some(([key, value]) => {
        if (key === "validation_link_status") return value && value !== "all";
        return String(value || "").trim() !== "";
      }),
    [appliedFilters]
  );

  const fetchRecords = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getDprValidationSupportRecords({
        page,
        per_page: perPage,
        sort_by: sortBy,
        sort_direction: sortDir,
        ...appliedFilters,
      });

      const nextRows = response.data?.data || [];
      setRows(nextRows);
      setSelected((prev) =>
        prev.filter((id) => nextRows.some((row) => Number(row.dpr_transaction_id) === Number(id)))
      );
      setSummary(response.data?.summary || defaultSummary);
      const pagination = response.data?.pagination || {};
      setTotalPages(pagination.total_pages || 1);
      setTotalRecords(pagination.total_records || 0);
    } catch (error) {
      setRows([]);
      setSummary(defaultSummary);
      setMessage({ type: "error", text: getErrorMessage(error, "Failed to load support records.") });
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters, page, perPage, sortBy, sortDir]);

  const fetchAuditLogs = useCallback(async () => {
    try {
      setAuditLoading(true);
      const response = await getDprValidationSupportAuditLogs({
        page: auditPage,
        per_page: 10,
        ...auditFilters,
      });
      setAuditRows(response.data?.data || []);
      const pagination = response.data?.pagination || {};
      setAuditTotalPages(pagination.total_pages || 1);
      setAuditTotalRecords(pagination.total_records || 0);
    } catch (error) {
      setAuditRows([]);
      setMessage({ type: "error", text: getErrorMessage(error, "Failed to load audit logs.") });
    } finally {
      setAuditLoading(false);
    }
  }, [auditFilters, auditPage]);

  useEffect(() => {
    if (activeTab === "records") fetchRecords();
  }, [activeTab, fetchRecords]);

  useEffect(() => {
    if (activeTab === "audit") fetchAuditLogs();
  }, [activeTab, fetchAuditLogs]);

  const showingText = useMemo(() => {
    if (!totalRecords) return "No records";
    const from = (page - 1) * perPage + 1;
    const to = Math.min(page * perPage, totalRecords);
    return `Showing ${from} to ${to} of ${totalRecords}`;
  }, [page, perPage, totalRecords]);

  const auditShowingText = useMemo(() => {
    if (!auditTotalRecords) return "No audit records";
    const from = (auditPage - 1) * 10 + 1;
    const to = Math.min(auditPage * 10, auditTotalRecords);
    return `Showing ${from} to ${to} of ${auditTotalRecords}`;
  }, [auditPage, auditTotalRecords]);

  const allPageSelected = rows.length > 0 && rows.every((row) => selected.includes(Number(row.dpr_transaction_id)));

  const setFilterValue = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPreview(null);
  };

  const applyFilters = () => {
    setAppliedFilters({ ...filters });
    setSelected([]);
    setPreview(null);
    setPage(1);
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setSelected([]);
    setPreview(null);
    setPage(1);
    setSortBy("processed_at");
    setSortDir("desc");
  };

  const toggleSelect = (id) => {
    const numericId = Number(id);
    setSelected((prev) =>
      prev.includes(numericId) ? prev.filter((item) => item !== numericId) : [...prev, numericId]
    );
    setPreview(null);
  };

  const selectAllOnPage = () => {
    if (allPageSelected) {
      setSelected([]);
      setPreview(null);
      return;
    }
    setSelected(rows.map((row) => Number(row.dpr_transaction_id)));
    setPreview(null);
  };

  const buildPayload = () => ({
    mode: updateMode,
    record_ids: updateMode === "selected" ? selected : [],
    filters: appliedFilters,
    field: targetField,
    new_value: newValue,
    set_null: selectedFieldMeta.nullable ? shouldSetNull : false,
  });

  const previewUpdate = async () => {
    setMessage(null);
    setPreview(null);

    if (updateMode === "selected" && selected.length === 0) {
      setMessage({ type: "error", text: "Select at least one row." });
      return;
    }
    if (updateMode === "filters" && !hasAppliedFilters) {
      setMessage({ type: "error", text: "Apply at least one filter for filter-matched update." });
      return;
    }
    if (!shouldSetNull && targetField === "remarks" && !newValue.trim()) {
      setMessage({ type: "error", text: "Remarks is required." });
      return;
    }

    try {
      setPreviewLoading(true);
      const response = await previewDprValidationSupportUpdate(buildPayload());
      setPreview(response.data?.data || null);
      setMessage({ type: "success", text: "Preview loaded." });
    } catch (error) {
      setMessage({ type: "error", text: getErrorMessage(error, "Preview failed.") });
    } finally {
      setPreviewLoading(false);
    }
  };

  const executeUpdate = async () => {
    if (!preview) {
      setMessage({ type: "error", text: "Run preview first." });
      return;
    }

    const confirmed = window.confirm(
      `Update ${preview.dpr_transaction_count || 0} DPR upload row(s) and ${preview.validation_upload_count || 0} validation_upload row(s)?`
    );
    if (!confirmed) return;

    try {
      setUpdateLoading(true);
      setMessage(null);
      const response = await executeDprValidationSupportUpdate(buildPayload());
      setMessage({
        type: "success",
        text: response.data?.message || "Support update completed.",
      });
      setPreview(null);
      setSelected([]);
      await fetchRecords();
      if (activeTab === "audit") {
        await fetchAuditLogs();
      }
    } catch (error) {
      setMessage({ type: "error", text: getErrorMessage(error, "Support update failed.") });
    } finally {
      setUpdateLoading(false);
    }
  };

  const applyAuditFilters = () => {
    setAuditPage(1);
    fetchAuditLogs();
  };

  const resetAuditFilters = () => {
    setAuditFilters(defaultAuditFilters);
    setAuditPage(1);
  };

  const updateAuditFilter = (key, value) => {
    setAuditFilters((prev) => ({ ...prev, [key]: value }));
  };

  const parseAuditJson = (value) => {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">DPR Validation Support</h1>
          <p className="text-sm text-gray-600">Scoped corrections for uploaded DPR validation records</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setActiveTab("records")}
            className={`rounded-lg border px-3 py-2 text-sm ${
              activeTab === "records" ? "border-gray-900 bg-gray-900 text-white" : "border-gray-200 bg-white hover:bg-gray-50"
            }`}
          >
            <FontAwesomeIcon icon={faClipboardCheck} className="mr-2" />
            Records
          </button>
          <button
            onClick={() => setActiveTab("audit")}
            className={`rounded-lg border px-3 py-2 text-sm ${
              activeTab === "audit" ? "border-gray-900 bg-gray-900 text-white" : "border-gray-200 bg-white hover:bg-gray-50"
            }`}
          >
            <FontAwesomeIcon icon={faHistory} className="mr-2" />
            Audit Logs
          </button>
          <button
            onClick={activeTab === "records" ? fetchRecords : fetchAuditLogs}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50"
          >
            <FontAwesomeIcon icon={activeTab === "records" && isLoading ? faSpinner : faSyncAlt} spin={activeTab === "records" && isLoading} className="mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`mb-4 rounded-lg border p-3 text-sm ${
            message.type === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-red-200 bg-red-50 text-red-700"
          }`}
        >
          <FontAwesomeIcon icon={message.type === "success" ? faCheckCircle : faExclamationTriangle} className="mr-2" />
          {message.text}
          <button className="float-right text-current" onClick={() => setMessage(null)} title="Dismiss">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {activeTab === "records" ? (
        <>
          <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-4">
            <SummaryBox label="Total" value={summary.total_rows} tone="text-gray-900" />
            <SummaryBox label="Active in DPR" value={summary.active_validation_rows} tone="text-emerald-700" />
            <SummaryBox label="Inactive" value={summary.inactive_validation_rows} tone="text-amber-700" />
            <SummaryBox label="Missing Link" value={summary.missing_validation_rows} tone="text-red-700" />
          </div>

          <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <FontAwesomeIcon icon={faFilter} />
              Filters
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <FilterInput label="Search" value={filters.search} onChange={(value) => setFilterValue("search", value)} placeholder="Upload, TRN, LOA, remarks" />
              <FilterInput label="Upload ID" value={filters.upload_id} onChange={(value) => setFilterValue("upload_id", value)} placeholder="Exact upload id" />
              <FilterInput label="Trans No." value={filters.trans_no} onChange={(value) => setFilterValue("trans_no", value)} placeholder="Exact trans no" />
              <FilterInput label="LOA No." value={filters.loa_no} onChange={(value) => setFilterValue("loa_no", value)} placeholder="Exact LOA" />
              <FilterInput label="Validate Status" value={filters.validate_status} onChange={(value) => setFilterValue("validate_status", value)} placeholder="Exact status" />
              <FilterInput label="Remarks" value={filters.remarks} onChange={(value) => setFilterValue("remarks", value)} placeholder="Exact current remarks" />
              <FilterInput label="Physician" value={filters.physician} onChange={(value) => setFilterValue("physician", value)} placeholder="Exact current physician" />
              <FilterInput label="Payment Ref. No." value={filters.payment_ref_no} onChange={(value) => setFilterValue("payment_ref_no", value)} placeholder="Exact PRN" />
              <FilterInput label="Validated By" value={filters.validated_by} onChange={(value) => setFilterValue("validated_by", value)} placeholder="V1 user id" />
              <FilterInput label="Uploaded By" value={filters.uploaded_by} onChange={(value) => setFilterValue("uploaded_by", value)} placeholder="Username or name" />
              <FilterSelect label="Validation Link" value={filters.validation_link_status} options={linkStatusOptions} onChange={(value) => setFilterValue("validation_link_status", value)} />
              <FilterInput label="Date From" type="date" value={filters.date_from} onChange={(value) => setFilterValue("date_from", value)} />
              <FilterInput label="Date To" type="date" value={filters.date_to} onChange={(value) => setFilterValue("date_to", value)} />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={applyFilters} className="rounded-lg bg-gray-900 px-3 py-2 text-sm text-white">
                <FontAwesomeIcon icon={faSearch} className="mr-2" />
                Search
              </button>
              <button onClick={resetFilters} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50">
                Reset
              </button>
            </div>
          </div>

          <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-5">
              <FilterSelect
                label="Apply Mode"
                value={updateMode}
                options={[
                  { value: "selected", label: "Selected rows" },
                  { value: "filters", label: "Filter matched rows" },
                ]}
                onChange={(value) => {
                  setUpdateMode(value);
                  setPreview(null);
                }}
              />
              <FilterSelect
                label="Target Field"
                value={targetField}
                options={fieldOptions.map((item) => ({ value: item.field, label: item.label }))}
                onChange={(value) => {
                  setTargetField(value);
                  setShouldSetNull(false);
                  setPreview(null);
                }}
              />
              <div className="lg:col-span-2">
                <label className="mb-1 block text-xs text-gray-600">New Value</label>
                <input
                  value={newValue}
                  onChange={(event) => {
                    setNewValue(event.target.value);
                    setPreview(null);
                  }}
                  disabled={shouldSetNull}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm disabled:bg-gray-100"
                  placeholder={`New ${selectedFieldMeta.label}`}
                />
              </div>
              <div className="flex items-end gap-2">
                {selectedFieldMeta.nullable && (
                  <label className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={shouldSetNull}
                      onChange={(event) => {
                        setShouldSetNull(event.target.checked);
                        setPreview(null);
                      }}
                    />
                    Set NULL
                  </label>
                )}
                <button
                  onClick={previewUpdate}
                  disabled={previewLoading}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={previewLoading ? faSpinner : faSearch} spin={previewLoading} className="mr-2" />
                  Preview
                </button>
                <button
                  onClick={executeUpdate}
                  disabled={!preview || updateLoading}
                  className="rounded-lg bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={updateLoading ? faSpinner : faWrench} spin={updateLoading} className="mr-2" />
                  Update
                </button>
              </div>
            </div>

            {preview && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Preview: {preview.dpr_transaction_count} DPR upload row(s), {preview.validation_upload_count} linked validation_upload row(s), field {preview.target_label}, new value {displayValue(preview.new_value)}.
              </div>
            )}
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 p-3 text-sm text-gray-600">
              <span>{showingText}</span>
              <div className="flex flex-wrap items-center gap-2">
                <span>Selected: {selected.length}</span>
                <span>Sort</span>
                <select value={sortBy} onChange={(event) => setSortBy(event.target.value)} className="rounded-lg border border-gray-200 px-2 py-1">
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <select value={sortDir} onChange={(event) => setSortDir(event.target.value)} className="rounded-lg border border-gray-200 px-2 py-1">
                  <option value="desc">Desc</option>
                  <option value="asc">Asc</option>
                </select>
                <span>Per page</span>
                <select
                  value={perPage}
                  onChange={(event) => {
                    setPerPage(Number(event.target.value));
                    setPage(1);
                  }}
                  className="rounded-lg border border-gray-200 px-2 py-1"
                >
                  {[10, 25, 50, 100].map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="overflow-auto">
              <table className="min-w-[1500px] w-full text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="p-3 text-left">
                      <input type="checkbox" checked={allPageSelected} onChange={selectAllOnPage} />
                    </th>
                    <th className="p-3 text-left">Link</th>
                    <th className="p-3 text-left">Upload</th>
                    <th className="p-3 text-left">Transaction</th>
                    <th className="p-3 text-left">Validation</th>
                    <th className="p-3 text-left">Support Fields</th>
                    <th className="p-3 text-right">Amount</th>
                    <th className="p-3 text-left">Users</th>
                    <th className="p-3 text-left">Dates</th>
                    <th className="p-3 text-right">DPR Rows</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {isLoading ? (
                    <tr>
                      <td colSpan="10" className="p-4 text-center text-gray-500">
                        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="p-4 text-center text-gray-500">No DPR validation support records found.</td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.dpr_transaction_id} className="align-top">
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={selected.includes(Number(row.dpr_transaction_id))}
                            onChange={() => toggleSelect(row.dpr_transaction_id)}
                          />
                        </td>
                        <td className="p-3">
                          <span className={linkStatusBadge(row.validation_link_status)}>{row.validation_link_status}</span>
                          <div className="mt-2 text-xs text-gray-500">VU: {displayValue(row.validation_upload_id)}</div>
                          <div className="text-xs text-gray-500">status: {displayValue(row.validation_upload_status)}</div>
                        </td>
                        <td className="p-3 max-w-xs">
                          <div className="font-medium text-gray-900">#{row.dpr_transaction_id}</div>
                          <div className="break-all text-xs text-gray-500">{displayValue(row.upload_id)}</div>
                          <div className="text-xs text-gray-500">CSV row: {displayValue(row.row_number)}</div>
                          <div className="break-words text-xs text-gray-500">{displayValue(row.original_file_name)}</div>
                        </td>
                        <td className="p-3">
                          <div className="font-medium text-gray-900">{displayValue(row.trans_no)}</div>
                          <div className="text-xs text-gray-500">LOA: {displayValue(row.loa_no)}</div>
                        </td>
                        <td className="p-3">
                          <div>{displayValue(row.validate_status)}</div>
                          <div className="mt-1 text-xs text-gray-500">VU status: {displayValue(row.validation_validate_status)}</div>
                        </td>
                        <td className="p-3 max-w-sm">
                          <FieldLine label="Remarks" value={row.remarks} compareValue={row.validation_remarks} />
                          <FieldLine label="Physician" value={row.physician} compareValue={row.validation_physician} />
                          <FieldLine label="Payment Ref" value={row.payment_ref_no} compareValue={row.validation_payment_ref_no} />
                        </td>
                        <td className="p-3 text-right">{formatAmount(row.transaction_amt)}</td>
                        <td className="p-3">
                          <div className="text-xs text-gray-500">Validated By</div>
                          <div>{displayValue(row.validated_by)}</div>
                          <div className="mt-2 text-xs text-gray-500">Uploaded By</div>
                          <div>{displayValue(row.uploaded_by_username)}</div>
                          <div className="text-xs text-gray-500">{displayValue(row.uploaded_by_name)}</div>
                        </td>
                        <td className="p-3">
                          <DateLine label="Processed" value={row.processed_at} />
                          <DateLine label="Validated" value={row.datetime_validated} />
                        </td>
                        <td className="p-3 text-right">{displayValue(row.dpr_rows_count)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between p-3 text-sm text-gray-600">
              <button onClick={() => setPage((prev) => Math.max(1, prev - 1))} disabled={page === 1} className="rounded-lg border border-gray-200 px-3 py-1 disabled:opacity-50">
                Previous
              </button>
              <span>Page {page} of {totalPages}</span>
              <button onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} disabled={page === totalPages} className="rounded-lg border border-gray-200 px-3 py-1 disabled:opacity-50">
                Next
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
              <FilterSelect label="Field" value={auditFilters.target_field} options={[{ value: "", label: "All" }, ...fieldOptions.map((item) => ({ value: item.field, label: item.label }))]} onChange={(value) => updateAuditFilter("target_field", value)} />
              <FilterSelect label="Status" value={auditFilters.status} options={[{ value: "", label: "All" }, { value: "success", label: "Success" }, { value: "failed", label: "Failed" }]} onChange={(value) => updateAuditFilter("status", value)} />
              <FilterInput label="Requested By" value={auditFilters.requested_by} onChange={(value) => updateAuditFilter("requested_by", value)} placeholder="Username" />
              <FilterInput label="Date From" type="date" value={auditFilters.date_from} onChange={(value) => updateAuditFilter("date_from", value)} />
              <FilterInput label="Date To" type="date" value={auditFilters.date_to} onChange={(value) => updateAuditFilter("date_to", value)} />
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={applyAuditFilters} className="rounded-lg bg-gray-900 px-3 py-2 text-sm text-white">
                <FontAwesomeIcon icon={faFilter} className="mr-2" />
                Apply Filters
              </button>
              <button onClick={resetAuditFilters} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50">
                Reset
              </button>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center justify-between p-3 text-sm text-gray-600">
              <span>{auditShowingText}</span>
              <button onClick={fetchAuditLogs} className="rounded-lg border border-gray-200 px-3 py-1 hover:bg-gray-50">
                <FontAwesomeIcon icon={auditLoading ? faSpinner : faSyncAlt} spin={auditLoading} className="mr-2" />
                Refresh
              </button>
            </div>
            <div className="overflow-auto">
              <table className="min-w-[1200px] w-full text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="p-3 text-left">Action</th>
                    <th className="p-3 text-left">Field</th>
                    <th className="p-3 text-left">Counts</th>
                    <th className="p-3 text-left">Requested By</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Created</th>
                    <th className="p-3 text-left">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {auditLoading ? (
                    <tr>
                      <td colSpan="7" className="p-4 text-center text-gray-500">
                        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                        Loading...
                      </td>
                    </tr>
                  ) : auditRows.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="p-4 text-center text-gray-500">No support audit logs found.</td>
                    </tr>
                  ) : (
                    auditRows.map((row) => {
                      const newValues = parseAuditJson(row.new_values_json);
                      const filtersJson = parseAuditJson(row.filters_json);
                      const transactionIds = parseAuditJson(row.dpr_transaction_ids_json);
                      const isExpanded = expandedAuditId === row.id;

                      return (
                        <React.Fragment key={row.id}>
                          <tr className="align-top">
                            <td className="p-3">
                              <div className="font-medium text-gray-900">#{row.id}</div>
                              <div className="break-all text-xs text-gray-500">{displayValue(row.action_uuid)}</div>
                            </td>
                            <td className="p-3">
                              <div>{displayValue(row.target_field)}</div>
                              <div className="text-xs text-gray-500">New: {displayValue(newValues?.new_value)}</div>
                            </td>
                            <td className="p-3">
                              <div>DPR upload: {displayValue(row.dpr_transactions_affected)}</div>
                              <div>validation_upload: {displayValue(row.validation_upload_affected)}</div>
                            </td>
                            <td className="p-3">
                              <div>{displayValue(row.requested_by_username)}</div>
                              <div className="text-xs text-gray-500">User ID: {displayValue(row.requested_by_user_id)}</div>
                              <div className="text-xs text-gray-500">{displayValue(row.ip_address)}</div>
                            </td>
                            <td className="p-3">
                              <span className={row.status === "success" ? "rounded-full bg-emerald-100 px-2 py-1 text-xs text-emerald-700" : "rounded-full bg-red-100 px-2 py-1 text-xs text-red-700"}>
                                {row.status}
                              </span>
                            </td>
                            <td className="p-3">{formatDate(row.created_at)}</td>
                            <td className="p-3">
                              <button onClick={() => setExpandedAuditId(isExpanded ? null : row.id)} className="rounded-lg border border-gray-200 px-2 py-1 text-xs hover:bg-gray-50">
                                {isExpanded ? "Hide" : "View"}
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan="7" className="bg-gray-50 p-3">
                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                  <JsonBox title="Filters" value={filtersJson} />
                                  <JsonBox title="DPR Upload Row IDs" value={transactionIds} />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between p-3 text-sm text-gray-600">
              <button onClick={() => setAuditPage((prev) => Math.max(1, prev - 1))} disabled={auditPage === 1} className="rounded-lg border border-gray-200 px-3 py-1 disabled:opacity-50">
                Previous
              </button>
              <span>Page {auditPage} of {auditTotalPages}</span>
              <button onClick={() => setAuditPage((prev) => Math.min(auditTotalPages, prev + 1))} disabled={auditPage === auditTotalPages} className="rounded-lg border border-gray-200 px-3 py-1 disabled:opacity-50">
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const SummaryBox = ({ label, value, tone }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4">
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-2xl font-semibold ${tone}`}>{value || 0}</div>
  </div>
);

const FilterInput = ({ label, value, onChange, type = "text", placeholder = "" }) => (
  <div>
    <label className="mb-1 block text-xs text-gray-600">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
      placeholder={placeholder}
    />
  </div>
);

const FilterSelect = ({ label, value, options, onChange }) => (
  <div>
    <label className="mb-1 block text-xs text-gray-600">{label}</label>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  </div>
);

const FieldLine = ({ label, value, compareValue }) => {
  const isMismatch = String(value || "") !== String(compareValue || "");
  return (
    <div className="mb-2">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="break-words text-gray-900">{displayValue(value)}</div>
      {isMismatch && (
        <div className="mt-1 break-words text-xs text-amber-700">validation_upload: {displayValue(compareValue)}</div>
      )}
    </div>
  );
};

const DateLine = ({ label, value }) => (
  <div className="mb-1">
    <span className="text-xs text-gray-500">{label}: </span>
    <span>{formatDate(value)}</span>
  </div>
);

const JsonBox = ({ title, value }) => (
  <div>
    <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</div>
    <pre className="max-h-64 overflow-auto rounded-lg bg-white p-3 text-xs text-gray-700">
      {JSON.stringify(value || {}, null, 2)}
    </pre>
  </div>
);

export default DprValidationSupportPage;
